/**
 * PII (Personally Identifiable Information) masking utilities
 *
 * These utilities detect anonymized PII values from privacy anonymization
 * and display a user-friendly "(redacted)" instead of the hash or literal.
 */

const REDACTED_DISPLAY = "(redacted)";

/**
 * Checks if a value appears to be a hashed PII value.
 * Hashed values follow the format: hashed_[hash]@anonymized.com or hashed_[hash]
 *
 * @param value - The value to check
 * @returns true if the value appears to be hashed
 */
export function isHashedValue(value: string | null | undefined): boolean {
  return typeof value === "string" && value.startsWith("hashed_");
}

/**
 * Checks if a value is the literal "redacted" string used for names.
 *
 * @param value - The value to check
 * @returns true if the value is the literal "redacted"
 */
export function isRedactedLiteral(value: string | null | undefined): boolean {
  return value === "redacted";
}

/**
 * Checks if a value has been anonymized (either hashed or literal "redacted").
 *
 * @param value - The value to check
 * @returns true if the value has been anonymized
 */
export function isAnonymizedValue(value: string | null | undefined): boolean {
  return isHashedValue(value) || isRedactedLiteral(value);
}

/**
 * Returns "(redacted)" if the value is hashed, otherwise returns the original value.
 * This prevents displaying cryptographic hashes to end users.
 *
 * @param value - The value to potentially mask
 * @returns The original value or "(redacted)" if hashed
 */
export function maskIfHashed(value: string | null | undefined): string {
  if (!value) return "";
  if (isHashedValue(value)) return REDACTED_DISPLAY;
  return value;
}

/**
 * Returns "(redacted)" if the value is anonymized (hashed or literal "redacted"),
 * otherwise returns the original value. Use this for name fields.
 *
 * @param value - The value to potentially mask
 * @returns The original value or "(redacted)" if anonymized
 */
export function maskIfAnonymized(value: string | null | undefined): string {
  if (!value) return "";
  if (isAnonymizedValue(value)) return REDACTED_DISPLAY;
  return value;
}
